/**
 * MagneticDynamics — pure mass-spring dynamics for magnetic pull-in.
 *
 * Each layout node carries a small dynamics record that tracks its
 * displacement from rest (the radial layout position), a velocity, and a
 * spring target. When a node is selected as the new focus, the host
 * writes target offsets into the records of its already-visible
 * neighbours so they elastically pull in toward the selection.
 * Clearing all targets lets every spring settle back to rest.
 *
 * Ported verbatim (with light renaming) from
 * `services/twg-microsite-prototype/src/experiments/magnetic-focus/twg-magnetic-dynamics.ts`
 * and `twg-graph-magnetic-focus.tsx::computePullInTargets`.
 */

import { OBJECT_NODE_HEIGHT, estimateObjectNodeWidth } from '../nodeSizing';

// ============================================================================
// Types
// ============================================================================

export interface NodeDynState {
	/** Displacement from rest, in graph-space pixels. */
	dispX: number;
	dispY: number;
	/** Velocity, in graph-space pixels / second. */
	vx: number;
	vy: number;
	/** Current spring target (also expressed as a displacement from rest). */
	targetX: number;
	targetY: number;
	/** When true, the host owns this node's position (drag); skip integration. */
	dragging: boolean;
}

export interface DynamicsCfg {
	/** Self-spring stiffness toward `target`. Units: 1/s^2. */
	kSelf: number;
	/**
	 * Exponential damping coefficient (1/s). Velocity decays as
	 * `v *= exp(-damping * dt)` per substep — stable for any dt.
	 */
	damping: number;
	/** Hard cap on integrated dt per frame so spikes can't explode the sim. */
	maxDt: number;
	/** Number of fixed substeps per frame (>=1). */
	substeps: number;
}

export const DEFAULT_DYNAMICS_CFG: DynamicsCfg = {
	kSelf: 80,
	damping: 6,
	maxDt: 1 / 30,
	substeps: 2,
};

export type DynStateMap = Map<string, NodeDynState>;

/** Slot describing where a single neighbour should be pulled to. */
export interface TargetSlot {
	id: string;
	targetX: number;
	targetY: number;
}

const REST_EPSILON = 0.01;

// ============================================================================
// State management
// ============================================================================

function makeEntry(): NodeDynState {
	return {
		dispX: 0,
		dispY: 0,
		vx: 0,
		vy: 0,
		targetX: 0,
		targetY: 0,
		dragging: false,
	};
}

/**
 * Add missing node entries and remove stale ones, so the state map mirrors
 * the current set of nodes. Existing entries are preserved (their disp/
 * velocity survive reveal-cycles). Returns the same map for convenience.
 */
export function syncDynState(state: DynStateMap, nodeIds: readonly string[]): DynStateMap {
	const want = new Set(nodeIds);
	for (const id of want) {
		if (!state.has(id)) state.set(id, makeEntry());
	}
	for (const id of state.keys()) {
		if (!want.has(id)) state.delete(id);
	}
	return state;
}

/**
 * Replace the targets of every entry in `state` with the values from
 * `slots`. Entries not present in `slots` have their target reset to
 * zero so they spring back to rest. Drag-pinned entries are left alone.
 */
export function setTargets(state: DynStateMap, slots: readonly TargetSlot[]): void {
	const slotMap = new Map(slots.map((s) => [s.id, s]));
	for (const [id, entry] of state) {
		const next = slotMap.get(id);
		if (next) {
			entry.targetX = next.targetX;
			entry.targetY = next.targetY;
		} else {
			entry.targetX = 0;
			entry.targetY = 0;
		}
	}
}

/** Zero every entry's spring target so all nodes spring back to rest. */
export function clearAllTargets(state: DynStateMap): void {
	for (const entry of state.values()) {
		entry.targetX = 0;
		entry.targetY = 0;
	}
}

/** Drop a node's spring state to rest immediately (used on grab/drag). */
export function pinToRest(state: DynStateMap, id: string): void {
	const entry = state.get(id);
	if (!entry) return;
	entry.dispX = 0;
	entry.dispY = 0;
	entry.vx = 0;
	entry.vy = 0;
	entry.targetX = 0;
	entry.targetY = 0;
}

/** Mark a node as currently being dragged (skipped by the integrator). */
export function setDragging(state: DynStateMap, id: string, dragging: boolean): void {
	const entry = state.get(id);
	if (!entry) return;
	entry.dragging = dragging;
}

// ============================================================================
// Integrator
// ============================================================================

/**
 * Integrate the spring system by `dt` seconds. Mutates `state` in place.
 *
 * Algorithm per substep (`h = dt / substeps`):
 *   1. Self spring : v += -kSelf * (disp - target) * h    (skip if dragging)
 *   2. Damp        : v *= exp(-damping * h)
 *   3. Integrate   : disp += v * h
 *
 * Snaps to rest when disp + velocity fall below `REST_EPSILON` and the
 * target is also zero, so idle springs don't churn the rAF loop forever.
 *
 * Returns `true` if any state changed measurably this frame — callers
 * can use this to skip the per-frame `setNodes` write when the system
 * is fully at rest.
 */
export function integrate(
	state: DynStateMap,
	cfg: DynamicsCfg,
	dt: number,
	// Accepts any object exposing `.has(id)`. Callers may pass a
	// `Set<string>` (the original contract) or any `Map<string, ...>`
	// — both have a structurally-compatible `.has()`. Used by
	// `useRadialMagneticLayout` which now stores per-pending-id metadata
	// (origin, restPosition) in a Map alongside the id set semantics.
	skipIds?: { has: (id: string) => boolean },
): boolean {
	if (dt <= 0) return false;
	const stepDt = Math.min(dt, cfg.maxDt) / Math.max(cfg.substeps, 1);
	const decay = Math.exp(-Math.max(cfg.damping, 0) * stepDt);
	const kSelfH = cfg.kSelf * stepDt;

	let active = false;

	for (let step = 0; step < cfg.substeps; step++) {
		if (kSelfH > 0) {
			for (const [id, s] of state) {
				if (s.dragging) continue;
				if (skipIds?.has(id)) continue;
				s.vx -= kSelfH * (s.dispX - s.targetX);
				s.vy -= kSelfH * (s.dispY - s.targetY);
			}
		}

		for (const [id, s] of state) {
			if (s.dragging) continue;
			// Skip nodes ReactFlow hasn't measured yet. Their `disp`
			// stays at zero (they paint at REST) until the measurement-
			// subscription effect in `useRadialMagneticLayout` seeds
			// `disp` from the captured origin and removes the id from
			// `pendingMeasureRef`. From the next tick the spring then
			// decays the *full* origin → rest displacement, so the user
			// sees the node visibly fly out of the centre rather than
			// appearing already half-decayed.
			if (skipIds?.has(id)) continue;
			s.vx *= decay;
			s.vy *= decay;
			s.dispX += s.vx * stepDt;
			s.dispY += s.vy * stepDt;

			if (
				s.targetX === 0 &&
				s.targetY === 0 &&
				Math.abs(s.dispX) < REST_EPSILON &&
				Math.abs(s.dispY) < REST_EPSILON &&
				Math.abs(s.vx) < REST_EPSILON &&
				Math.abs(s.vy) < REST_EPSILON
			) {
				s.dispX = 0;
				s.dispY = 0;
				s.vx = 0;
				s.vy = 0;
			} else if (
				s.targetX !== 0 ||
				s.targetY !== 0 ||
				Math.abs(s.dispX) >= REST_EPSILON ||
				Math.abs(s.dispY) >= REST_EPSILON ||
				Math.abs(s.vx) >= REST_EPSILON ||
				Math.abs(s.vy) >= REST_EPSILON
			) {
				active = true;
			}
		}
	}

	return active;
}

// ============================================================================
// Pull-in target computation
// ============================================================================

const REVEAL_ARC_SPREAD = Math.PI * 2; // always full circle for consistent spread

export interface NodeSize {
	width: number;
	height: number;
}

export interface PullInInput {
	selectedId: string;
	/**
	 * Visual center of the selected node in graph-space. The ring is
	 * centered here so all ring slots are equidistant from the node's
	 * edges regardless of its width/height.
	 */
	selectedRest: { x: number; y: number };
	/**
	 * Size of the selected node. Used to enforce a minimum edge gap so
	 * no neighbour slot lands closer than `MIN_RING_EDGE_GAP` px from
	 * the selected node's bounding box edge.
	 */
	selectedSize?: NodeSize;
	/** Already-revealed neighbour ids of the selection. */
	neighbourIds: string[];
	/** id → rest position lookup for every revealed node. */
	restPositions: Map<string, { x: number; y: number }>;
	/** Optional: id of the original constellation focus (root user node). */
	focusNodeId?: string;
	/** Optional: id of the node currently being dragged. */
	draggedNodeId?: string | null;
	/** Ring radius (graph-space) — computed from `sizes` when omitted. */
	ringRadius?: number;
	/**
	 * Aspect ratio of the ring (1 = circular, > 1 = wider than tall).
	 * Useful when the focused node is wider/taller than typical.
	 */
	ringAspect?: number;
	/**
	 * Measured/estimated sizes for every revealed node. When supplied,
	 * the ring radius scales with the summed arc-widths of eligible
	 * neighbours so the pull-in ends up on the same spacing as the
	 * layout engine produces — no cramming for many neighbours, no
	 * exaggerated gaps for few.
	 */
	sizes?: Map<string, NodeSize>;
}

/** Fallback ring radius used when `sizes` isn't supplied. */
const REVEAL_RING_RADIUS = 220;
/** Base semi-axes for the size-aware ring — matches `RadialLayoutEngine`. */
const PULL_IN_BASE_RX = 260;
const PULL_IN_BASE_RY = 220;
/** Must track `RadialLayoutEngine.MIN_GAP_PX`. */
const PULL_IN_MIN_GAP = 28;
/**
 * Minimum edge-to-edge gap (px) enforced between the selected node's
 * bounding box and any ring slot. Prevents ring slots landing nearly
 * on top of the selected node when it is wide/tall relative to the
 * ring radius.
 */
const MIN_RING_EDGE_GAP = 60;
const PULL_IN_FALLBACK_SIZE: NodeSize = {
	width: estimateObjectNodeWidth(null),
	height: OBJECT_NODE_HEIGHT,
};

export function seedNodeFromOrigin(
	state: DynStateMap,
	id: string,
	restPosition: { x: number; y: number },
	originPosition: { x: number; y: number },
): void {
	const entry = state.get(id);
	if (!entry || entry.dragging) return;

	entry.dispX = originPosition.x - restPosition.x;
	entry.dispY = originPosition.y - restPosition.y;
	entry.vx = 0;
	entry.vy = 0;
}

function ellipseCircumference(rx: number, ry: number): number {
	return Math.PI * (3 * (rx + ry) - Math.sqrt((3 * rx + ry) * (rx + 3 * ry)));
}

/**
 * Local mirror of `RadialLayoutEngine.computeRingRadius`. Kept private
 * to avoid a hard dep between the dynamics layer and the layout engine
 * (they're conceptually distinct: engine writes base positions, dynamics
 * drives displacements).
 */
function sizeAwareRingRadius(
	ids: readonly string[],
	sizes: Map<string, NodeSize>,
	baseRx: number,
	baseRy: number,
	minGap: number,
): { rx: number; ry: number } {
	if (ids.length === 0) return { rx: baseRx, ry: baseRy };
	const totalArc = ids.reduce((sum, id) => {
		const size = sizes.get(id) ?? PULL_IN_FALLBACK_SIZE;
		return sum + size.width + minGap;
	}, 0);
	const baseCircumference = ellipseCircumference(baseRx, baseRy);
	if (totalArc <= baseCircumference) return { rx: baseRx, ry: baseRy };
	const scale = totalArc / baseCircumference;
	return { rx: baseRx * scale, ry: baseRy * scale };
}

/**
 * Compute the spring `target` for every already-visible neighbour of the
 * selected node. Each slot places a neighbour on a ring around the
 * selection, fanned outward away from the focus. Returned offsets are
 * displacements from each neighbour's *rest* position — exactly what
 * `setTargets` consumes.
 *
 * When `sizes` is supplied, the ring radius scales with the measured
 * widths of eligible neighbours (same math as `RadialLayoutEngine`), so
 * the pull-in settles them onto the same spacing as the layout engine
 * would — regardless of how many siblings exist. Without `sizes`, we
 * fall back to the legacy flat `REVEAL_RING_RADIUS`.
 */
export function computePullInTargets(input: PullInInput): TargetSlot[] {
	const {
		selectedId,
		selectedRest,
		selectedSize,
		neighbourIds,
		restPositions,
		focusNodeId,
		draggedNodeId,
		ringRadius,
		ringAspect,
		sizes,
	} = input;

	const eligibleIds = neighbourIds.filter((id) => {
		if (id === selectedId) return false;
		if (draggedNodeId && id === draggedNodeId) return false;
		return restPositions.has(id);
	});
	if (eligibleIds.length === 0) return [];

	// Resolve the ring:
	//   1. Explicit `ringRadius` override wins (caller supplies rx + aspect).
	//   2. Otherwise, if `sizes` is available, scale the ring to hold
	//      every eligible neighbour comfortably — same math the layout
	//      engine uses.
	//   3. Otherwise, fall back to the flat legacy constant.
	let rx: number;
	let ry: number;
	if (ringRadius !== undefined) {
		rx = ringRadius * (ringAspect ?? 1);
		ry = ringRadius;
	} else if (sizes) {
		const ring = sizeAwareRingRadius(
			eligibleIds,
			sizes,
			PULL_IN_BASE_RX,
			PULL_IN_BASE_RY,
			PULL_IN_MIN_GAP,
		);
		rx = ring.rx;
		ry = ring.ry;
	} else {
		rx = REVEAL_RING_RADIUS * (ringAspect ?? 1);
		ry = REVEAL_RING_RADIUS;
	}

	// Enforce a minimum edge-to-edge gap between the selected node's
	// bounding box and any neighbour's bounding box at its ring slot.
	//
	// `selectedRest` is the selected node's visual center. Ring slots are
	// placed at (selectedRest + rx·cosθ, selectedRest + ry·sinθ), which
	// is the *center* of the neighbour at that slot. So the edge gap in
	// the horizontal axis is:
	//
	//   gap = rx - selectedSize.width/2 - avgNeighbourSize.width/2
	//
	// We want gap ≥ MIN_RING_EDGE_GAP, so:
	//   rx ≥ selectedSize.width/2 + avgNeighbourSize.width/2 + MIN_RING_EDGE_GAP
	//
	// and similarly for ry / height. Using the average neighbour size is
	// a reasonable approximation — extreme outliers may still be tight,
	// but the ring won't be systematically too small for the typical case.
	if (selectedSize) {
		const avgNeighbourW =
			eligibleIds.length > 0
				? eligibleIds.reduce((sum, id) => {
						const s = sizes?.get(id) ?? PULL_IN_FALLBACK_SIZE;
						return sum + s.width;
					}, 0) / eligibleIds.length
				: PULL_IN_FALLBACK_SIZE.width;
		const avgNeighbourH =
			eligibleIds.length > 0
				? eligibleIds.reduce((sum, id) => {
						const s = sizes?.get(id) ?? PULL_IN_FALLBACK_SIZE;
						return sum + s.height;
					}, 0) / eligibleIds.length
				: PULL_IN_FALLBACK_SIZE.height;

		const minRx = selectedSize.width / 2 + avgNeighbourW / 2 + MIN_RING_EDGE_GAP;
		const minRy = selectedSize.height / 2 + avgNeighbourH / 2 + MIN_RING_EDGE_GAP;
		if (rx < minRx) rx = minRx;
		if (ry < minRy) ry = minRy;
	}

	// Bias the fan outward from the original focus, mirroring the
	// reveal-layout direction so the pulled-in cluster appears on the
	// same side as new reveals.
	const focusPos = focusNodeId ? restPositions.get(focusNodeId) : undefined;
	const inwardAngle =
		focusPos && (focusPos.x !== selectedRest.x || focusPos.y !== selectedRest.y)
			? Math.atan2(focusPos.y - selectedRest.y, focusPos.x - selectedRest.x)
			: null;
	const fanCentre = inwardAngle !== null ? inwardAngle + Math.PI : -Math.PI / 2;

	const arcSpread = eligibleIds.length >= 6 ? Math.PI * 2 : REVEAL_ARC_SPREAD;
	const useFullCircle = arcSpread >= Math.PI * 2 - 1e-3;

	return eligibleIds.map((id, index) => {
		const restPos = restPositions.get(id);
		if (!restPos) return { id, targetX: 0, targetY: 0 };

		const count = eligibleIds.length;
		const t = useFullCircle ? index / count : count === 1 ? 0.5 : index / (count - 1);
		const angle = useFullCircle
			? fanCentre + t * Math.PI * 2
			: fanCentre - arcSpread / 2 + t * arcSpread;

		// `selectedRest` is the selected node's visual center, so the ring
		// slot is a center position. The displacement must move the
		// *neighbour's center* to the slot — not its top-left corner.
		// `restPos` from `RadialLayoutEngine` is the node's top-left, so
		// the rest center = (restPos.x + w/2, restPos.y + h/2).
		const size = sizes?.get(id) ?? PULL_IN_FALLBACK_SIZE;
		const restCenterX = restPos.x + size.width / 2;
		const restCenterY = restPos.y + size.height / 2;

		const targetVisualX = selectedRest.x + Math.cos(angle) * rx;
		const targetVisualY = selectedRest.y + Math.sin(angle) * ry;

		return {
			id,
			targetX: targetVisualX - restCenterX,
			targetY: targetVisualY - restCenterY,
		};
	});
}
